import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  FaExclamationTriangle, 
  FaServer, 
  FaCheckCircle, 
  FaSearch,
  FaInfoCircle,
  FaLink,
  FaTimes
} from 'react-icons/fa';
import { api } from '../../services/api';
import * as Sentry from "@sentry/react";
import { useAuth } from '../../components/auth/AuthContext';

// ============================================================================
// SENTRY LOGGING HELPERS
// ============================================================================
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'firewall-attach-modal' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'firewall-attach-modal' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'firewall-attach-modal',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// ============================================================================
// ERROR MESSAGES MAPPING
// ============================================================================
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'USER_NOT_FOUND': 'User profile not found. Please refresh and try again',
  'ACCESS_DENIED': 'You do not have permission to perform this action',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'INVALID_USER_PROFILE': 'Invalid user profile format. Please contact support',
  'INCOMPLETE_PROFILE': 'Please complete your profile to continue',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable. Please try again later',
  'DB_ERROR': 'Service temporarily unavailable. Please try again later',
  'API_ERROR': 'Service temporarily unavailable. Please try again later',
  'INVALID_API_RESPONSE': 'Service error. Please try again later',
  'API_CONNECTION_ERROR': 'Unable to connect to service. Please check your connection and try again',
  'NETWORK_ERROR': 'Unable to connect to service. Please check your connection and try again',
  'INVALID_ID_FORMAT': 'Invalid request format. Please try again',
  'INVALID_DATA': 'Invalid input data. Please check your entries and try again',
  'INVALID_REQUEST': 'Invalid request format. Please try again',
  'MISSING_FIELDS': 'Please fill in all required fields',
  'VALIDATION_ERROR': 'Please check your input and try again',
  'INVALID_RULE_PARAMETERS': 'Invalid firewall rule parameters. Please check your input',
  'INVALID_JSON': 'Invalid request format. Please check your input',
  'UNEXPECTED_PAYLOAD': 'Unexpected request data. Please try again',
  'UNEXPECTED_BODY': 'Invalid request format. Please try again',
  'INVALID_RULE_ID_FORMAT': 'Invalid rule ID format. Please try again',
  'FIREWALL_LIST_FAILED': 'Unable to load firewalls. Please refresh the page',
  'FIREWALL_DETAILS_FAILED': 'Unable to load firewall details. Please try again',
  'FIREWALL_CREATE_FAILED': 'Unable to create firewall. Please try again',
  'FIREWALL_DELETE_FAILED': 'Unable to delete firewall. Please try again',
  'FIREWALL_NOT_FOUND': 'Firewall not found. It may have been deleted',
  'FIREWALL_LIMIT_EXCEEDED': 'You have reached the maximum limit of 50 firewalls',
  'FIREWALL_HAS_ATTACHMENTS': 'Firewall is attached to VMs. Please detach before deleting',
  'FIREWALL_IN_USE': 'Firewall is currently in use. Please detach all instances before deleting',
  'RESOURCE_ALREADY_EXISTS': 'A firewall with this name already exists',
  'FIREWALL_RULE_ADD_FAILED': 'Unable to add firewall rule. Please try again',
  'FIREWALL_RULE_DELETE_FAILED': 'Unable to remove firewall rule. Please try again',
  'FIREWALL_RULE_NOT_FOUND': 'Firewall rule not found. It may have been removed',
  'SECURITY_RULE_ADD_FAILED': 'Unable to add security rule. Please try again',
  'SECURITY_RULE_DELETE_FAILED': 'Unable to remove security rule. Please try again',
  'SECURITY_RULE_ALREADY_EXISTS': 'This security rule already exists for this instance',
  'RULE_NOT_FOUND': 'Security rule not found. It may have been removed',
  'VM_LIST_FAILED': 'Unable to load virtual machines. Please refresh the page',
  'VM_NOT_FOUND': 'Virtual machine not found. It may have been deleted',
  'VM_NOT_ACTIVE': 'Virtual machine must be active to perform this action',
  'VM_FIREWALLS_FAILED': 'Unable to load VM firewall information. Please try again',
  'VM_EVENTS_FAILED': 'Unable to load VM events. Please try again',
  'FIREWALL_ATTACH_FAILED': 'Unable to attach firewall to VMs. Please try again',
  'NO_VMS_SELECTED': 'Please select at least one virtual machine',
  'INVALID_VM_IDS': 'Some selected VMs are invalid or not found',
  'VM_NOT_COMPATIBLE': 'Selected VMs are not compatible with this firewall (different regions)',
  'INSTANCE_NOT_FOUND': 'Instance not found. It may have been deleted',
  'INSTANCE_NOT_AVAILABLE': 'Instance is not available for this operation',
  'INSTANCE_NOT_COMPATIBLE': 'Selected instances are not compatible with this firewall',
  'INSTANCES_NOT_ACTIVE': 'Some instances are not active. Only active instances can be modified',
  'INSTANCE_NOT_ACTIVE': 'Instance must be active to perform this action',
  'INVALID_INSTANCE_ID_FORMAT': 'Invalid instance ID format. Expected format: BRK-XXXXXXD',
  'TIER_NOT_SUPPORTED': 'This operation is only supported for legacy tier instances',
  'REGION_NOT_AVAILABLE': 'Selected region is not available. Please choose a different region',
  'INVALID_REGION_FORMAT': 'Invalid region format. Please try again',
  'UNSUPPORTED_REGION': 'This region is not supported for firewalls',
  'PROTOCOLS_FAILED': 'Unable to load supported protocols. Please refresh the page',
  'REGIONS_FAILED': 'Unable to load available regions. Please refresh the page',
  // Network Errors (NEW)
'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again."
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  if (error.response?.status === 401 || error.status === 401) {
    return ERROR_MESSAGES['AUTH_ERROR'];
  }
  
  return 'Something went wrong. Please try again.';
};
// Network error detection with 6-position priority
const getNetworkErrorMessage = (error) => {
  // Position 2.1: Browser knows it's offline
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.2: Request timeout
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  // Position 2.3: Network error but browser thinks it's online
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  // Position 2.4: Network error and browser now offline
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.5: Request sent but no response received
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  // Position 2.6: Fallback
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// ============================================================================
// RESPONSIVE HOOK
// ============================================================================
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  return { isMobile, isTablet };
};

// ============================================================================
// SHIELD ICON COMPONENT
// ============================================================================
const ShieldIcon = ({ size = 20, color = '#1a1a1a' }) => (
  <svg width={size} height={size + 4} viewBox="0 0 24 28" fill={color}>
    <path d="M12 0L0 4v10c0 6 5 12 12 14 7-2 12-8 12-14V4L12 0z"/>
  </svg>
);

// ============================================================================
// REFRESH ICON COMPONENT
// ============================================================================
const RefreshIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"/>
  </svg>
);

// ============================================================================
// INLINE STYLES
// ============================================================================
const getStyles = (isSubmitting = false) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '16px',
  },
  modal: {
    backgroundColor: '#121212',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '680px',
    border: '1px solid #2a2a2a',
    overflow: 'hidden',
    display: 'flex',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  leftPanel: {
    flex: 1,
    padding: '28px',
    borderRight: '1px solid #2a2a2a',
    opacity: isSubmitting ? 0.6 : 1,
    transition: 'opacity 0.15s',
    pointerEvents: isSubmitting ? 'none' : 'auto',
  },
  rightPanel: {
    width: '220px',
    padding: '28px',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '24px',
  },
  iconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: isSubmitting 
      ? 'linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%)'
      : 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(224,234,204,0.3)',
    transition: 'all 0.15s',
  },
  title: {
    color: 'white',
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#6b7280',
    margin: 0,
    fontSize: '13px',
  },
  label: {
    color: '#9ca3af',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '8px',
    display: 'block',
  },
  searchWrapper: {
    position: 'relative',
    marginBottom: '16px',
  },
  searchIcon: {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6b7280',
  },
  input: {
    width: '100%',
    padding: '12px 14px 12px 44px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  vmList: {
    maxHeight: '220px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  vmCard: (selected, disabled, attached) => ({
    padding: '14px',
    backgroundColor: attached ? 'rgba(34, 197, 94, 0.08)' : selected ? '#1c2b22' : '#1a1a1a',
    border: `1px solid ${attached ? 'rgba(34, 197, 94, 0.4)' : selected ? '#e0eacc' : '#333'}`,
    borderRadius: '10px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled && !attached ? 0.5 : 1,
    transition: 'all 0.15s',
  }),
  vmName: {
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    margin: 0,
  },
  vmMeta: {
    color: '#6b7280',
    fontSize: '11px',
    margin: '2px 0 0 0',
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    backgroundColor: 'rgba(202,217,238,0.1)',
    color: '#cad9ee',
    border: '1px solid rgba(202,217,238,0.3)',
  },
  radio: (selected) => ({
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: `2px solid ${selected ? '#e0eacc' : '#555'}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }),
  radioInner: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#e0eacc',
  },
  summaryHeader: {
    color: '#6b7280',
    fontSize: '11px',
    letterSpacing: '1.5px',
    margin: '0 0 24px 0',
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: '11px',
    margin: '0 0 4px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  summaryValue: {
    color: 'white',
    fontSize: '14px',
    margin: '0 0 16px 0',
    wordBreak: 'break-all',
  },
  submitBtn: (disabled) => ({
    width: '100%',
    padding: '14px 24px',
    backgroundColor: disabled ? '#3d4435' : '#e0eacc',
    color: disabled ? '#a0a89a' : 'black',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: disabled ? 'none' : '0 4px 14px rgba(224,234,204,0.25)',
    marginTop: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.15s',
  }),
  cancelBtn: (disabled) => ({
    width: '100%',
    marginTop: '10px',
    padding: '12px 24px',
    backgroundColor: '#333',
    color: disabled ? '#666' : 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.15s',
  }),
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(0,0,0,0.2)',
    borderTopColor: 'black',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  spinnerLight: {
    width: '32px',
    height: '32px',
    border: '3px solid #333',
    borderTopColor: '#e0eacc',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    marginBottom: '16px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  },
  emptyTitle: {
    color: 'white',
    fontSize: '15px',
    fontWeight: '500',
    margin: '0 0 8px 0',
  },
  emptyText: {
    color: '#6b7280',
    fontSize: '13px',
    margin: 0,
    lineHeight: '1.5',
  },
  infoBox: {
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '8px',
    padding: '10px 12px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  infoText: {
    color: '#93c5fd',
    fontSize: '12px',
  },
  errorBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '12px 14px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  loadingText: {
    color: '#9ca3af',
    fontSize: '14px',
    margin: 0,
  },
  refreshBtn: (disabled) => ({
    background: 'none',
    border: 'none',
    color: disabled ? '#444' : '#6b7280',
    fontSize: '12px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    borderRadius: '6px',
    transition: 'all 0.15s',
  }),
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const FirewallAttachModal = ({ isOpen, onClose, firewall, onAttachmentUpdated }) => {
  const { handleAuthError } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vms, setVms] = useState([]);
  const [selectedVmId, setSelectedVmId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [fetchingVms, setFetchingVms] = useState(true);
  const [alreadyAttachedVms, setAlreadyAttachedVms] = useState([]);
  
  // Refs
  const abortControllerRef = useRef(null);
  const maxRetries = 3;
  const retryAttempts = useRef(0);
  
  // Derived values
  const styles = getStyles(loading);
  
  // Memoized filtered VMs
  const filteredVms = useMemo(() => {
    if (!Array.isArray(vms)) return [];
    if (!searchTerm.trim()) return vms;
    return vms.filter(vm => 
      vm.name && vm.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [vms, searchTerm]);
  
  // Memoized selected VM
  const selectedVm = useMemo(() => {
    return vms.find(vm => vm.server_uuid === selectedVmId);
  }, [vms, selectedVmId]);
  
  // Memoized validation
  const canSubmit = useMemo(() => {
    return selectedVmId && !loading && !fetchingVms;
  }, [selectedVmId, loading, fetchingVms]);

  // Sentry context
  useEffect(() => {
    if (isOpen && firewall) {
      Sentry.setContext("firewall-attach-modal", {
        firewallId: firewall.id,
        firewallName: firewall.name,
        vmCount: vms.length,
        selectedVmId,
        alreadyAttachedCount: alreadyAttachedVms.length,
        isLoading: loading,
        isFetchingVms: fetchingVms,
        isMobile,
        isTablet
      });
    }
  }, [isOpen, firewall, vms.length, selectedVmId, alreadyAttachedVms.length, loading, fetchingVms, isMobile, isTablet]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        logInfo('Cleanup: Aborted pending firewall attach modal requests');
      }
    };
  }, []);

const debouncedFetchVms = useCallback(
  debounce(async () => {
    const startTime = performance.now();
    
    logInfo('Initiating VMs fetch for firewall attachment', {
      firewallId: firewall?.id,
      firewallName: firewall?.name
    });

    if (retryAttempts.current >= maxRetries) {
      setError('Maximum refresh attempts reached. Please wait before trying again.');
      logError('Maximum VM fetch attempts exceeded', {
        attempts: retryAttempts.current,
        firewallId: firewall?.id
      });
      return;
    }
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    retryAttempts.current++;
    setFetchingVms(true);
    setError(null);
    
    try {
      const response = await api.listVmsForFirewall();
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (abortControllerRef.current?.signal.aborted) {
        logInfo('VMs fetch request was aborted', { duration });
        return;
      }
      
      if (response.success) {
        const filteredVms = (response.instances || []).filter(vm => vm.server_uuid);
        setVms(filteredVms);
        retryAttempts.current = 0;

        logPerformance('fetch_vms_for_firewall', duration, {
          firewallId: firewall?.id,
          vmCount: filteredVms.length,
          success: true
        });

        logInfo('VMs loaded successfully', {
          count: filteredVms.length,
          firewallId: firewall?.id,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        const safeMessage = getSafeErrorMessage(response);
        setError(safeMessage);
        
        logError('Failed to fetch VMs', {
          firewallId: firewall?.id,
          code: response?.code,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        logInfo('VMs fetch request aborted by user');
        return;
      }
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401 || err.code === 'AUTH_ERROR') {
        logError('Authentication error during VMs fetch', {
          firewallId: firewall?.id,
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        });
        handleAuthError();
        return;
      }
      
      // Position 2: Use ERROR_MESSAGES lookup if code exists and has mapping
      if (err.code && ERROR_MESSAGES[err.code]) {
        setError(ERROR_MESSAGES[err.code]);
        logError('VMs fetch error', {
          firewallId: firewall?.id,
          code: err.code,
          status: err.status,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        return;
      }
      
      // Position 3: Use interceptor's user-friendly message directly
      if (err.error) {
        setError(err.error);
        logError('VMs fetch error', {
          firewallId: firewall?.id,
          code: err.code,
          status: err.status,
          message: err.error,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        return;
      }
      
      // Position 4: Fallback for unexpected error format
      setError('Something went wrong. Please try again.');
      logError('VMs fetch error (unexpected format)', {
        firewallId: firewall?.id,
        errorType: typeof err,
        duration: `${duration.toFixed(2)}ms`
      }, err);
    } finally {
      setFetchingVms(false);
    }
  }, 300),
  [firewall, handleAuthError]
);
  // Focus/Visibility refresh
  useEffect(() => {
    const handleFocus = () => {
      if (isOpen && firewall) {
        logInfo('Refreshing VMs data on page focus');
        debouncedFetchVms();
      }
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden && isOpen && firewall) {
        logInfo('Refreshing VMs data on visibility change');
        debouncedFetchVms();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, firewall, debouncedFetchVms]);

  // Initialize on modal open
  useEffect(() => {
    if (isOpen && firewall) {
      logInfo('Firewall attach modal opened', {
        firewallId: firewall.id,
        firewallName: firewall.name,
        attachmentCount: firewall.attachments?.length || 0,
        isMobile,
        isTablet
      });

      logUserAction('firewall_attach_modal_opened', {
        firewallId: firewall.id,
        firewallName: firewall.name
      });

      debouncedFetchVms();
      
      // Reset state
      setSelectedVmId('');
      setSearchTerm('');
      setError(null);
      
      // Get already attached VMs
      if (firewall.attachments && Array.isArray(firewall.attachments)) {
        const attachedVmIds = firewall.attachments
          .filter(attachment => attachment.vm && attachment.vm.id)
          .map(attachment => attachment.vm.id.toString());
        setAlreadyAttachedVms(attachedVmIds);

        logInfo('Already attached VMs identified', {
          firewallId: firewall.id,
          attachedCount: attachedVmIds.length
        });
      } else {
        setAlreadyAttachedVms([]);
      }
    }
  }, [isOpen, firewall, debouncedFetchVms, isMobile, isTablet]);

  // Check if VM is attached
  const isVmAttached = useCallback((vmId) => {
    return alreadyAttachedVms.includes(vmId?.toString());
  }, [alreadyAttachedVms]);

  // Handle VM selection
  const handleVmSelection = useCallback((vmId) => {
    if (isVmAttached(vmId)) {
      logInfo('Attempted to select already attached VM', { vmId, firewallId: firewall?.id });
      return;
    }
    
    logUserAction('vm_selected_for_firewall_attachment', {
      vmId,
      firewallId: firewall?.id,
      previousSelection: selectedVmId
    });
    
    setSelectedVmId(vmId);
    setError(null);
    
    logInfo('VM selected for firewall attachment', {
      vmId,
      firewallId: firewall?.id
    });
  }, [isVmAttached, selectedVmId, firewall?.id]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    logUserAction('refresh_vms_for_firewall', {
      firewallId: firewall?.id,
      currentVmCount: vms.length,
      isMobile,
      isTablet
    });
    
    logInfo('Refreshing VMs list for firewall attachment', {
      firewallId: firewall?.id,
      currentVmCount: vms.length
    });
    
    retryAttempts.current = 0;
    debouncedFetchVms();
  }, [firewall?.id, vms.length, debouncedFetchVms, isMobile, isTablet]);

const handleSubmit = useCallback(async () => {
  const startTime = performance.now();
  
  logUserAction('firewall_attachment_submitted', {
    firewallId: firewall?.id,
    selectedVmId
  });
  
  if (!selectedVmId) {
    const safeMessage = getSafeErrorMessage({ code: 'NO_VMS_SELECTED' });
    setError(safeMessage);
    logError('Firewall attachment failed - no VM selected', { firewallId: firewall?.id });
    return;
  }
  
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
  
  abortControllerRef.current = new AbortController();
  
  setLoading(true);
  setError(null);
  
  try {
    const response = await api.attachFirewallToVMs(firewall.id, [selectedVmId]);
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (abortControllerRef.current?.signal.aborted) {
      logInfo('Firewall attachment request was aborted', { duration });
      return;
    }
    
    if (response.success) {
      if (onAttachmentUpdated) onAttachmentUpdated();
      onClose();

      logPerformance('attach_firewall_to_vm', duration, {
        firewallId: firewall.id,
        vmId: selectedVmId,
        success: true
      });

      logInfo('Firewall attachment completed successfully', {
        firewallId: firewall.id,
        vmId: selectedVmId,
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('firewall_attachment_success', {
        firewallId: firewall.id,
        vmId: selectedVmId
      });
    } else {
      const safeMessage = getSafeErrorMessage(response);
      setError(safeMessage);
      
      logError('Failed to attach firewall to VM', {
        firewallId: firewall.id,
        vmId: selectedVmId,
        code: response?.code,
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('firewall_attachment_failed', {
        firewallId: firewall.id,
        vmId: selectedVmId
      });
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      logInfo('Firewall attachment request aborted by user');
      return;
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      logError('Authentication error during firewall attachment', {
        firewallId: firewall.id,
        vmId: selectedVmId,
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      });
      handleAuthError();
      return;
    }
    
    // Position 2: Use ERROR_MESSAGES lookup if code exists and has mapping
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Firewall attachment error', {
        firewallId: firewall.id,
        vmId: selectedVmId,
        code: err.code,
        status: err.status,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 3: Use interceptor's user-friendly message directly
    if (err.error) {
      setError(err.error);
      logError('Firewall attachment error', {
        firewallId: firewall.id,
        vmId: selectedVmId,
        code: err.code,
        status: err.status,
        message: err.error,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 4: Fallback for unexpected error format
    setError('Something went wrong. Please try again.');
    logError('Firewall attachment error (unexpected format)', {
      firewallId: firewall.id,
      vmId: selectedVmId,
      errorType: typeof err,
      duration: `${duration.toFixed(2)}ms`
    }, err);

    logUserAction('firewall_attachment_error', {
      firewallId: firewall.id,
      vmId: selectedVmId
    });
  } finally {
    setLoading(false);
  }
}, [selectedVmId, firewall, onAttachmentUpdated, onClose, handleAuthError]);  // Handle close
  const handleClose = useCallback(() => {
    if (loading) return;
    
    logUserAction('firewall_attach_modal_closed', {
      firewallId: firewall?.id,
      selectedVmId,
      hadSelection: !!selectedVmId
    });
    
    logInfo('Firewall attach modal closed', {
      firewallId: firewall?.id,
      selectedVmId,
      vmCount: vms.length
    });
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    onClose();
  }, [firewall?.id, selectedVmId, vms.length, onClose, loading]);

  // Get modal styles based on device
  const getModalStyles = useCallback(() => {
    if (isMobile) {
      return {
        width: 'calc(100% - 1rem)',
        maxWidth: '100%',
        maxHeight: '90vh',
        margin: '0.5rem',
        flexDirection: 'column',
      };
    } else if (isTablet) {
      return {
        width: 'calc(100% - 2rem)',
        maxWidth: '600px',
        maxHeight: '85vh',
        margin: '1rem',
        flexDirection: 'column',
      };
    }
    return {};
  }, [isMobile, isTablet]);

  // Render left panel content
  const renderLeftPanelContent = () => {
    if (fetchingVms) {
      return (
        <div style={styles.loadingContainer}>
          <div style={styles.spinnerLight} />
          <p style={styles.loadingText}>Loading virtual machines...</p>
        </div>
      );
    }

    if (!vms || vms.length === 0) {
      return (
        <div style={styles.emptyState}>
          <div style={{ marginBottom: '16px' }}>
            <FaServer size={44} color="#555" />
          </div>
          <p style={styles.emptyTitle}>No Virtual Machines Found</p>
          <p style={styles.emptyText}>
            You haven't created any virtual machines yet.<br />
            Create a VM first to attach this firewall.
          </p>
        </div>
      );
    }

    return (
      <>
        {error && (
          <div style={styles.errorBox}>
            <FaExclamationTriangle size={16} style={{ color: '#ef4444', marginTop: '2px', flexShrink: 0 }} />
            <span style={{ color: '#fca5a5', fontSize: '13px' }}>{sanitizeText(error)}</span>
          </div>
        )}
        
        <div style={styles.infoBox}>
          <FaInfoCircle size={16} color="#60a5fa" />
          <span style={styles.infoText}>Firewall rules will apply immediately after attachment</span>
        </div>
        
        <label style={styles.label}>Select Virtual Machine</label>
        <div style={styles.searchWrapper}>
          <FaSearch size={14} style={styles.searchIcon} />
         <input
  type="text"
  placeholder="Search VMs..."
  value={searchTerm}
  onChange={(e) => setSearchTerm(e.target.value)}
  maxLength={30}
  style={styles.input}
  disabled={loading}
  onFocus={(e) => {
    if (!loading) {
      e.target.style.borderColor = '#e0eacc';
      e.target.style.backgroundColor = '#252525';
      e.target.style.boxShadow = '0 0 0 3px rgba(224, 234, 204, 0.15)';
    }
  }}
  onBlur={(e) => {
    e.target.style.borderColor = '#333';
    e.target.style.backgroundColor = '#1a1a1a';
    e.target.style.boxShadow = 'none';
  }}
/>
        </div>

        {filteredVms.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ marginBottom: '16px' }}>
              <FaSearch size={36} color="#555" />
            </div>
            <p style={styles.emptyTitle}>No Results Found</p>
            <p style={styles.emptyText}>
              No VMs matching "{sanitizeText(searchTerm)}"<br />
              Try a different search term.
            </p>
          </div>
        ) : (
          <div style={styles.vmList} className="firewall-attach-scrollbar">
            {filteredVms.map(vm => {
              const isAttached = isVmAttached(vm.server_uuid);
              const isSelected = selectedVmId === vm.server_uuid;
              
              return (
                <div
                  key={vm.server_uuid}
                  style={styles.vmCard(isSelected, isAttached, isAttached)}
                  onClick={() => handleVmSelection(vm.server_uuid)}
                  onMouseEnter={(e) => {
                    if (!isAttached && !isSelected && !loading) {
                      e.currentTarget.style.borderColor = '#555';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isAttached && !isSelected) {
                      e.currentTarget.style.borderColor = '#333';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {isAttached ? (
                        <FaCheckCircle size={18} color="#22c55e" />
                      ) : (
                        <div style={styles.radio(isSelected)}>
                          {isSelected && <div style={styles.radioInner} />}
                        </div>
                      )}
                      <div>
                        <p style={styles.vmName}>{sanitizeText(vm.name)}</p>
                        <p style={styles.vmMeta}>
                          {isAttached ? (
                            <span style={{ color: '#22c55e' }}>Already attached</span>
                          ) : (
                            sanitizeText(vm.region)
                          )}
                        </p>
                      </div>
                    </div>
                    <span style={styles.statusBadge}>{sanitizeText(vm.status)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  if (!isOpen) return null;

  const firewallName = firewall?.name || '';
  const firewallRegion = firewall?.region || '';
  const firewallRulesCount = firewall?.rules?.length || firewall?.rules_count || 0;
  const firewallDescription = firewall?.description || '';

  const modalStyles = getModalStyles();
  const isMobileOrTablet = isMobile || isTablet;

  return (
    <>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .firewall-attach-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .firewall-attach-scrollbar::-webkit-scrollbar-track {
          background: #2a2a2a;
          border-radius: 3px;
        }
        .firewall-attach-scrollbar::-webkit-scrollbar-thumb {
          background: #555;
          border-radius: 3px;
        }
        .firewall-attach-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #666;
        }
      `}</style>
      
      <div style={styles.overlay}>
        <div style={{ ...styles.modal, ...modalStyles }}>
          {/* Mobile/Tablet Header with Close Button */}
          {isMobileOrTablet && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #2a2a2a',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <ShieldIcon size={18} color="#1a1a1a" />
                </div>
                <div>
                  <h2 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: '600' }}>Attach Firewall</h2>
                  <p style={{ color: '#6b7280', margin: 0, fontSize: '12px' }}>{sanitizeText(firewallName)}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '8px',
                }}
              >
                <FaTimes size={20} />
              </button>
            </div>
          )}

          {/* LEFT PANEL */}
          <div style={{
            ...styles.leftPanel,
            borderRight: isMobileOrTablet ? 'none' : '1px solid #2a2a2a',
            padding: isMobileOrTablet ? '20px' : '28px',
          }}>
            {/* Desktop Header */}
            {!isMobileOrTablet && (
              <div style={styles.header}>
                <div style={styles.iconWrapper}>
                  <ShieldIcon size={22} color={loading ? '#666' : '#1a1a1a'} />
                </div>
                <div>
                  <h2 style={styles.title}>Attach Firewall</h2>
                  <p style={styles.subtitle}>Connect to a virtual machine</p>
                </div>
              </div>
            )}

            {renderLeftPanelContent()}

            {/* Mobile/Tablet: Selection Info + Buttons */}
            {isMobileOrTablet && (
              <div style={{ marginTop: '20px' }}>
                {selectedVm && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#1c2b22',
                    border: '1px solid #e0eacc',
                    borderRadius: '8px',
                    marginBottom: '16px',
                  }}>
                    <p style={{ color: '#6b7280', fontSize: '11px', margin: '0 0 4px 0' }}>Selected VM</p>
                    <p style={{ color: '#e0eacc', fontSize: '14px', fontWeight: '500', margin: 0 }}>
                      {sanitizeText(selectedVm.name)}
                    </p>
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  style={{
                    ...styles.submitBtn(!canSubmit),
                    marginTop: 0,
                  }}
                >
                  {loading ? (
                    <>
                      <div style={styles.spinner} />
                      <span>Attaching...</span>
                    </>
                  ) : (
                    <>
                      <FaLink size={14} />
                      <span>Attach Firewall</span>
                    </>
                  )}
                </button>

                <button
                  onClick={handleClose}
                  disabled={loading}
                  style={styles.cancelBtn(loading)}
                >
                  Cancel
                </button>

                {/* Refresh Button */}
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <button
                    onClick={handleRefresh}
                    disabled={loading || fetchingVms}
                    style={styles.refreshBtn(loading || fetchingVms)}
                    onMouseEnter={(e) => {
                      if (!loading && !fetchingVms) {
                        e.currentTarget.style.color = '#e0eacc';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = (loading || fetchingVms) ? '#444' : '#6b7280';
                    }}
                  >
                    <RefreshIcon size={12} />
                    {fetchingVms ? 'Refreshing...' : 'Refresh VM List'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT PANEL - Desktop Only */}
          {!isMobileOrTablet && (
            <div style={styles.rightPanel}>
              <h3 style={styles.summaryHeader}>FIREWALL</h3>
              
              <p style={styles.summaryLabel}>Name</p>
              <p style={styles.summaryValue}>{sanitizeText(firewallName)}</p>
              
              <p style={styles.summaryLabel}>Region</p>
              <p style={{ ...styles.summaryValue, color: '#e0eacc' }}>{sanitizeText(firewallRegion)}</p>
              
              <p style={styles.summaryLabel}>Rules</p>
              <p style={styles.summaryValue}>{firewallRulesCount} rules configured</p>
              
              {firewallDescription && (
                <>
                  <p style={styles.summaryLabel}>Description</p>
                  <p style={{ 
                    ...styles.summaryValue, 
                    fontSize: '12px',
                    color: '#9ca3af',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}>
                    {sanitizeText(firewallDescription)}
                  </p>
                </>
              )}
              
              {selectedVm && (
                <div style={{ borderTop: '1px solid #222', paddingTop: '16px', marginTop: '8px' }}>
                  <p style={styles.summaryLabel}>Attaching To</p>
                  <p style={{ ...styles.summaryValue, color: '#e0eacc', marginBottom: '4px' }}>
                    {sanitizeText(selectedVm.name)}
                  </p>
                  <p style={{ color: '#6b7280', fontSize: '11px', margin: 0 }}>
                    {sanitizeText(selectedVm.status)} • {sanitizeText(selectedVm.region)}
                  </p>
                </div>
              )}
              
              <div style={{ flex: 1 }} />
              
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                style={styles.submitBtn(!canSubmit)}
              >
                {loading ? (
                  <>
                    <div style={styles.spinner} />
                    <span>Attaching...</span>
                  </>
                ) : (
                  <>
                    <FaLink size={14} />
                    <span>Attach Firewall</span>
                  </>
                )}
              </button>
              
              <button
                onClick={handleClose}
                disabled={loading}
                style={styles.cancelBtn(loading)}
              >
                Cancel
              </button>

              {/* Refresh Button */}
              <div style={{ 
                marginTop: '16px', 
                paddingTop: '16px', 
                borderTop: '1px solid #222',
                textAlign: 'center' 
              }}>
                <button
                  onClick={handleRefresh}
                  disabled={loading || fetchingVms}
                  style={styles.refreshBtn(loading || fetchingVms)}
                  onMouseEnter={(e) => {
                    if (!loading && !fetchingVms) {
                      e.currentTarget.style.color = '#e0eacc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = (loading || fetchingVms) ? '#444' : '#6b7280';
                  }}
                >
                  <RefreshIcon size={12} />
                  {fetchingVms ? 'Refreshing...' : 'Refresh VM List'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FirewallAttachModal;